import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO, isValid } from "date-fns";
import { useAuth0, type User } from "@auth0/auth0-react";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import {
  getKpiDataByArea,
  getKpiLinksByArea,
  patchKpiLink,
  type KpiAreaApiType,
} from "../../API/KPI/AreaKpi";
import type { AreaKpiSummary } from "../../Types/KPI/IAreaKpiSummary";
import type { KeyResult, KpiLink, KpiProgressNote } from "../../Types/KPI/IAreaKpi";
import type { Comment } from "../Utils/CommentsPopup/CommentsPopup";
import CommentItem from "../Utils/CommentsPopup/CommentItem";
import PillLabel from "../Utils/PillLabel/PillLabel";
import LoadingIcon from "../Utils/LoadingIcon/LoadingIcon";
import NoItems from "../Utils/NoItems/NoItems";
import styles from "./KpiAreaSummaryModal.module.css";

type Row = { kr: KeyResult; link: KpiLink };

const STATUS_OPTIONS = [
  { label: "Exceeded", colour: 6 },
  { label: "Met", colour: 1 },
  { label: "Working towards", colour: 2 },
  { label: "Will not achieve", colour: 0 },
] as const;

function sortNotesDesc(notes: KpiProgressNote[] | undefined): KpiProgressNote[] {
  if (!notes?.length) return [];
  return [...notes].sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    return db - da;
  });
}

/** Substring fallback when API label is not exactly one of {@link STATUS_OPTIONS}. */
function pillClassBySubstring(label: string | undefined): string {
  const l = (label ?? "").toLowerCase();
  if (l.includes("exceeded")) return styles.pillExceeded;
  if (l.includes("met") && !l.includes("not")) return styles.pillMet;
  if (l.includes("working")) return styles.pillWorking;
  if (l.includes("will not") || l.includes("not met")) return styles.pillRisk;
  return styles.pillNeutral;
}

/** Prefer exact KPI status labels so pills match {@link STATUS_OPTIONS} styling. */
function pillClassForKpiNote(label: string | undefined): string {
  const t = (label ?? "").trim();
  if (!t) return styles.pillNeutral;
  const low = t.toLowerCase();
  const exact = STATUS_OPTIONS.find((o) => o.label.toLowerCase() === low);
  if (exact) {
    switch (exact.label) {
      case "Exceeded":
        return styles.pillExceeded;
      case "Met":
        return styles.pillMet;
      case "Working towards":
        return styles.pillWorking;
      case "Will not achieve":
        return styles.pillRisk;
      default:
        return styles.pillNeutral;
    }
  }
  return pillClassBySubstring(t);
}

function formatNoteDate(value: string | undefined): string {
  if (!value) return "—";
  try {
    const d = value.includes("T") ? parseISO(value) : new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return format(d, "dd MMM yyyy");
  } catch {
    return "—";
  }
}

function colourForLabel(label: string): number {
  const found = STATUS_OPTIONS.find((o) => o.label === label);
  return found?.colour ?? 9;
}

/** Body text from embedded comment (handles API `CommentText` vs `commentText`). */
function noteCommentPlainText(note: KpiProgressNote): string {
  const c = note.comment;
  if (!c) return "";
  const ext = c as { CommentText?: string };
  return (c.commentText ?? ext.CommentText ?? "").trim();
}

function auth0IdentityStrings(user: User | undefined): string[] {
  if (!user) return [];
  const raw: string[] = [];
  for (const v of [user.name, user.nickname, user.email]) {
    const t = (v ?? "").trim().toLowerCase();
    if (t) raw.push(t);
  }
  const email = user.email?.trim();
  if (email) {
    const at = email.indexOf("@");
    if (at > 0) {
      const local = email.slice(0, at).toLowerCase();
      if (local) raw.push(local);
    }
  }
  return Array.from(new Set(raw));
}

function parseNoteCommentDate(note: KpiProgressNote): Date {
  const raw = note.comment?.dateMade || note.date;
  if (!raw) return new Date();
  try {
    const d = typeof raw === "string" && raw.includes("T") ? parseISO(raw) : new Date(raw);
    return isValid(d) ? d : new Date();
  } catch {
    return new Date();
  }
}

/** Progress note `owner` is set at save; falls back to embedded commenter if legacy data has no owner. */
function isProgressNoteOwnedByCurrentUser(note: KpiProgressNote, user: User | undefined): boolean {
  const aliases = auth0IdentityStrings(user);
  if (!aliases.length) return false;
  const owner = (note.owner ?? "").trim().toLowerCase();
  if (owner && aliases.includes(owner)) return true;
  const commenter = (note.comment?.commenter ?? "").trim().toLowerCase();
  return !!commenter && aliases.includes(commenter);
}

function kpiProgressNoteToComment(note: KpiProgressNote, canEditThisNote: boolean): Comment | null {
  const id = note.id;
  if (!id) return null;
  const commenter = (note.comment?.commenter ?? "").trim() || note.owner || "—";
  return {
    id,
    DateMade: parseNoteCommentDate(note),
    CommentText: noteCommentPlainText(note),
    Commenter: commenter,
    Editable: canEditThisNote,
  };
}

type KpiProgressNoteCardProps = {
  note: KpiProgressNote;
  user: User | undefined;
  hasKpiEdit: boolean;
  kpiReadOnlyMenuTooltip: string | undefined;
  onSaveComment: (id: string, text: string) => void;
  onDeleteProgressNote: (id: string) => void;
};

/** One progress note: header row + optional comment body; menu anchored in header when body uses CommentItem. */
function KpiProgressNoteCard({
  note,
  user,
  hasKpiEdit,
  kpiReadOnlyMenuTooltip,
  onSaveComment,
  onDeleteProgressNote,
}: KpiProgressNoteCardProps) {
  const [menuMountEl, setMenuMountEl] = useState<HTMLDivElement | null>(null);
  const [commentEditing, setCommentEditing] = useState(false);
  const label = note.status?.statusLabel?.trim() || "Not Set";
  const pv = note.status?.progressValue;
  const savedText = noteCommentPlainText(note);
  const isOwnNote = isProgressNoteOwnedByCurrentUser(note, user);
  const canMutateNote = hasKpiEdit && isOwnNote;
  const commentModel = kpiProgressNoteToComment(note, canMutateNote);
  /** Always mount menu when the note has an id; read-only/disabled state comes from CommentItem. */
  const showCommentItem = !!commentModel;
  const bodyChrome = !!savedText || commentEditing;

  return (
    <div className={styles.noteCard}>
      <div className={styles.noteTop}>
        <div className={styles.noteTopLeft}>
          <span className={styles.noteDate}>{formatNoteDate(note.date)}</span>
          <span className={styles.noteOwner}>{note.owner || "—"}</span>
        </div>
        <div className={styles.noteTopRight}>
          {pv != null && !Number.isNaN(pv) && (
            <span className={styles.progressPct}>{pv}%</span>
          )}
          <span className={[styles.pill, pillClassForKpiNote(label)].join(" ")}>{label}</span>
          {showCommentItem ? <div className={styles.noteMenuMount} ref={setMenuMountEl} /> : null}
        </div>
      </div>
      {showCommentItem && commentModel && (
        <div
          className={bodyChrome ? styles.noteCommentBlock : styles.noteCommentSlot}
        >
          <CommentItem
            comment={commentModel}
            metadataShownElsewhere
            menuMountEl={menuMountEl}
            omitEmptyKpiBody={!savedText}
            onEditingChange={setCommentEditing}
            onEdit={(id, text) => {
              onSaveComment(id, text);
            }}
            onDelete={(id) => {
              onDeleteProgressNote(id);
            }}
            canEdit={canMutateNote}
            canDelete={canMutateNote}
            readOnlyMenuTooltip={kpiReadOnlyMenuTooltip}
            deleteConfirmation={{
              title: "Delete progress note",
              message: "Delete this progress note? This cannot be undone.",
            }}
          />
        </div>
      )}
    </div>
  );
}

type Props = {
  /** When false, data loading is skipped (e.g. parent keeps mounted while hidden). */
  active?: boolean;
  areaId: string;
  areaType: KpiAreaApiType;
  /** Optional area-load KPI payload (from GetQuestionnaireByAreaId) to avoid extra KPI API calls. */
  prefetchedSummary?: AreaKpiSummary;
  clientId: string;
  initialKeyResultId?: string | null;
  hasKpiEdit: boolean;
  /** Role title from questionnaire permissions (for read-only KPI menu tooltips). */
  roleTitle?: string | null;
  onClose: () => void;
  /** Shown as the uppercase pill above the title (e.g. area / questionnaire name). */
  areaBadgeLabel?: string | null;
};

const KpiAreaSummaryModal: React.FC<Props> = ({
  active = true,
  areaId,
  areaType,
  prefetchedSummary,
  clientId,
  initialKeyResultId,
  hasKpiEdit,
  roleTitle,
  onClose: _onClose,
  areaBadgeLabel,
}) => {
  const { user } = useAuth0();
  const displayName = (user?.name ?? user?.nickname ?? user?.email ?? "").trim();

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [selectedKrId, setSelectedKrId] = useState<string | null>(null);

  const [newNoteDate, setNewNoteDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [newNoteLabel, setNewNoteLabel] = useState<string>(STATUS_OPTIONS[1].label);
  const [newNoteProgress, setNewNoteProgress] = useState<number>(0);
  const [newNoteCommentText, setNewNoteCommentText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (!active || !clientId || !areaId) {
      setRows([]);
      setSelectedKrId(null);
      setLoadError(null);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [linkList, krArea] = prefetchedSummary
          ? [prefetchedSummary.links ?? [], prefetchedSummary.keyResults ?? []]
          : await Promise.all([
              getKpiLinksByArea(areaType, areaId),
              getKpiDataByArea(areaType, areaId),
            ]);
        const order = new Map(krArea.map((k, i) => [k.id, i]));
        const keyResultById = new Map(krArea.map((kr) => [kr.id, kr]));
        const built: Row[] = [];
        for (const link of linkList) {
          if (!link.keyResultId || link.isDeleted) continue;
          const kr = keyResultById.get(link.keyResultId);
          if (!kr || kr.archived || kr.deleted) continue;
          built.push({ kr, link });
        }
        built.sort(
          (a, b) => (order.get(a.kr.id) ?? 999) - (order.get(b.kr.id) ?? 999),
        );
        if (cancelled) return;
        setRows(built);
        const ids = built.map((r) => r.kr.id);
        const pick =
          initialKeyResultId && ids.includes(initialKeyResultId)
            ? initialKeyResultId
            : ids[0] ?? null;
        setSelectedKrId(pick);
        setNewNoteDate(format(new Date(), "yyyy-MM-dd"));
        setNewNoteLabel(STATUS_OPTIONS[1].label);
        setNewNoteProgress(0);
        setNewNoteCommentText("");
      } catch {
        if (!cancelled) {
          setLoadError("Could not load key results for this area.");
          setRows([]);
          setSelectedKrId(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [active, areaId, areaType, prefetchedSummary, initialKeyResultId]);

  const selected = useMemo(
    () => rows.find((r) => r.kr.id === selectedKrId) ?? null,
    [rows, selectedKrId],
  );

  const sortedNotes = useMemo(
    () => sortNotesDesc(selected?.link.kpiProgressNotes),
    [selected?.link.kpiProgressNotes],
  );

  const headerMainTitle = useMemo(() => {
    if (selected?.kr.title?.trim()) return selected.kr.title.trim();
    return "Key results";
  }, [selected?.kr.title]);

  const badgeText = (areaBadgeLabel ?? "").trim();

  const kpiReadOnlyMenuTooltip = useMemo(() => {
    if (hasKpiEdit) return "You can only edit your own KPI Progress notes";
    const rt = (roleTitle ?? "").trim();
    return rt
      ? `Your role as ${rt} means you cannot edit progress notes`
      : "Your role means you cannot edit progress notes";
  }, [hasKpiEdit, roleTitle]);

  const canSaveNewNote =
    hasKpiEdit &&
    !!newNoteLabel?.trim() &&
    newNoteProgress >= 0 &&
    newNoteProgress <= 100 &&
    !Number.isNaN(newNoteProgress);

  const replaceRowLink = useCallback((keyResultId: string, link: KpiLink) => {
    setRows((prev) =>
      prev.map((r) => (r.kr.id === keyResultId ? { ...r, link } : r)),
    );
  }, []);

  const handleSaveNewNote = useCallback(async () => {
    if (!selected || !canSaveNewNote) return;
    const link = selected.link;
    if (!link.id) return;
    const trimmedComment = newNoteCommentText.trim();
    const note: KpiProgressNote = {
      id: uuidv4(),
      date: new Date(`${newNoteDate}T12:00:00`).toISOString(),
      owner: displayName,
      status: {
        statusLabel: newNoteLabel,
        statusColour: colourForLabel(newNoteLabel),
        progressValue: newNoteProgress,
      },
      comment: {
        id: uuidv4(),
        commentText: trimmedComment,
        CommentText: trimmedComment,
        commenter: displayName,
        dateMade: new Date().toISOString(),
        authorizationContext: areaId,
        contextId: selected.kr.id,
        deleted: false,
      },
    };
    setSavingNote(true);
    try {
      const saved = await patchKpiLink(clientId, link.id, {
        operation: "AddProgressNote",
        progressNote: note,
      });
      replaceRowLink(selected.kr.id, saved);
      toast.success("Progress note saved.");
      setNewNoteCommentText("");
    } catch {
      toast.error("Could not save progress note.");
    } finally {
      setSavingNote(false);
    }
  }, [
    selected,
    canSaveNewNote,
    newNoteDate,
    newNoteLabel,
    newNoteProgress,
    newNoteCommentText,
    displayName,
    clientId,
    areaId,
    replaceRowLink,
  ]);

  const handleSaveComment = useCallback(
    async (noteId: string, text: string) => {
      if (!selected || !hasKpiEdit) return;
      const link = selected.link;
      if (!link.id) return;
      const note = (link.kpiProgressNotes ?? []).find((n) => n.id === noteId);
      if (!note || !isProgressNoteOwnedByCurrentUser(note, user)) {
        toast.error("You can only edit your own progress notes.");
        return;
      }
      const trimmed = text.trim();
      const updatedComment = {
        ...(note.comment ?? { id: uuidv4() }),
        commentText: text,
        CommentText: text,
        commenter: note.comment?.commenter || displayName,
        dateMade: note.comment?.dateMade ?? new Date().toISOString(),
        authorizationContext: note.comment?.authorizationContext ?? areaId,
        contextId: note.comment?.contextId ?? selected.kr.id,
        deleted: trimmed ? false : note.comment?.deleted ?? false,
      };
      try {
        const saved = await patchKpiLink(clientId, link.id, {
          operation: "UpdateProgressNoteComment",
          progressNoteId: noteId,
          comment: updatedComment,
        });
        replaceRowLink(selected.kr.id, saved);
        toast.success("Comment saved.");
      } catch {
        toast.error("Could not save comment.");
      }
    },
    [selected, hasKpiEdit, user, displayName, clientId, areaId, replaceRowLink],
  );

  const handleDeleteProgressNote = useCallback(
    async (noteId: string) => {
      if (!selected || !hasKpiEdit) return;
      const link = selected.link;
      if (!link.id) return;
      const note = (link.kpiProgressNotes ?? []).find((n) => n.id === noteId);
      if (!note || !isProgressNoteOwnedByCurrentUser(note, user)) {
        toast.error("You can only delete your own progress notes.");
        return;
      }
      try {
        const saved = await patchKpiLink(clientId, link.id, {
          operation: "DeleteProgressNote",
          progressNoteId: noteId,
        });
        replaceRowLink(selected.kr.id, saved);
        toast.success("Progress note deleted.");
      } catch {
        toast.error("Could not delete progress note.");
      }
    },
    [selected, hasKpiEdit, user, clientId, replaceRowLink],
  );

  return (
    <div className={styles.wrap}>
      <header className={styles.popupHeader}>
        {loading ? (
          <div
            className={styles.headerLoading}
            role="status"
            aria-live="polite"
            aria-label="Loading"
          >
            <LoadingIcon />
          </div>
        ) : (
          <h1 className={styles.headerTitle}>{headerMainTitle}</h1>
        )}
        <div className={styles.headerDivider} aria-hidden />
      </header>

      {!loading && loadError && <p className={styles.error}>{loadError}</p>}
      {!loading && !loadError && rows.length === 0 && (
        <p className={styles.empty}>No KPI data found for this area.</p>
      )}

      {!loading && !loadError && rows.length > 0 && (
        <>
          {selected && (
            <div className={styles.panel}>

              <section className={styles.section} aria-labelledby="kpi-history-heading">
                {sortedNotes.length === 0 && (
                  <div className={styles.notesEmptyState}>
                    <NoItems message="No progress notes yet." />
                  </div>
                )}
                <div className={styles.noteList}>
                  {sortedNotes.map((note) => (
                    <KpiProgressNoteCard
                      key={note.id ?? `${note.date}-${note.owner}`}
                      note={note}
                      user={user}
                      hasKpiEdit={hasKpiEdit}
                      kpiReadOnlyMenuTooltip={kpiReadOnlyMenuTooltip}
                      onSaveComment={(id, text) => {
                        void handleSaveComment(id, text);
                      }}
                      onDeleteProgressNote={(id) => {
                        void handleDeleteProgressNote(id);
                      }}
                    />
                  ))}
                </div>
              </section>

              {hasKpiEdit && (
                <div className={styles.newNoteShell}>
                  <h3 className={styles.newNoteTitle}>Create a new progress note</h3>
                  <div className={styles.formGrid}>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel} htmlFor="kpi-new-date">
                        Date completed
                      </label>
                      <input
                        id="kpi-new-date"
                        className={styles.input}
                        type="date"
                        value={newNoteDate}
                        onChange={(e) => setNewNoteDate(e.target.value)}
                      />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel} htmlFor="kpi-new-status">
                        Status
                      </label>
                      <select
                        id="kpi-new-status"
                        className={styles.select}
                        value={newNoteLabel}
                        onChange={(e) => setNewNoteLabel(e.target.value)}
                      >
                        {STATUS_OPTIONS.map((o) => (
                          <option key={o.label} value={o.label}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={`${styles.field} ${styles.formGridFull}`}>
                      <label className={styles.fieldLabel} htmlFor="kpi-new-progress">
                        Progress
                      </label>
                      <div className={styles.progressFieldWrap}>
                        <input
                          id="kpi-new-progress"
                          className={styles.inputProgress}
                          type="number"
                          min={0}
                          max={100}
                          value={newNoteProgress}
                          onChange={(e) => setNewNoteProgress(Number(e.target.value))}
                          aria-describedby="kpi-progress-hint"
                        />
                        <span className={styles.progressSuffix} id="kpi-progress-hint">
                          %
                        </span>
                      </div>
                    </div>
                    <div className={`${styles.field} ${styles.formGridFull}`}>
                      <label className={styles.fieldLabel} htmlFor="kpi-new-comment">
                        Comment
                      </label>
                      <textarea
                        id="kpi-new-comment"
                        className={styles.commentTextarea}
                        placeholder="Optional — add context for this progress update…"
                        value={newNoteCommentText}
                        onChange={(e) => setNewNoteCommentText(e.target.value)}
                        rows={4}
                      />
                    </div>
                  </div>
                  <div className={styles.formActions}>
                    <button
                      type="button"
                      className={styles.btn}
                      disabled={!canSaveNewNote || savingNote}
                      onClick={() => void handleSaveNewNote()}
                    >
                      {savingNote ? "Posting…" : "Post progress note"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default KpiAreaSummaryModal;
